import type { ReactNode } from "react";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSortableAccessibility } from "./useSortableAccessibility";

export type SortableListProps = {
  /** One stable id per row, in display order — see `useSortableIds`. */
  ids: readonly string[];
  /** A drop landed: move the item at `from` to `to`. */
  onMove: (from: number, to: number) => void;
  /** What one row is, for the handle's accessible name ("action", "condition"). */
  itemLabel: string;
  /** Gap between rows, in theme spacing units. */
  spacing?: number;
  /** Renders one row; place `handle` wherever the row's design wants it. */
  children: (index: number, handle: ReactNode) => ReactNode;
};

const SortableItem = ({
  id,
  index,
  itemLabel,
  children,
}: {
  id: string;
  index: number;
  itemLabel: string;
  children: (handle: ReactNode) => ReactNode;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const handle = (
    <Tooltip title="Drag to reorder">
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        // After the spreads: dnd-kit sets a generic description, and which row
        // this is matters more than that it is sortable.
        aria-label={`reorder ${itemLabel} ${index + 1}`}
        sx={{ cursor: "grab", touchAction: "none" }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        // A dragged row floats over its neighbours rather than under them.
        zIndex: isDragging ? 1 : "auto",
        position: "relative",
      }}
    >
      {children(handle)}
    </Box>
  );
};

/**
 * A vertical list whose rows can be dragged into a new order.
 *
 * Drag is handle-only: the rows here are full of pickers and text boxes, and a
 * whole-row drag would swallow every click meant for them. The keyboard sensor
 * makes the same move available without a pointer.
 */
export const SortableList = ({
  ids,
  onMove,
  itemLabel,
  spacing = 2,
  children,
}: SortableListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const accessibility = useSortableAccessibility(ids, itemLabel);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onMove(from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={accessibility}
    >
      <SortableContext items={[...ids]} strategy={verticalListSortingStrategy}>
        <Stack spacing={spacing}>
          {ids.map((id, index) => (
            <SortableItem key={id} id={id} index={index} itemLabel={itemLabel}>
              {(handle) => children(index, handle)}
            </SortableItem>
          ))}
        </Stack>
      </SortableContext>
    </DndContext>
  );
};
